import React, { useEffect, useContext } from "react";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { makeStyles, createStyles } from "@material-ui/styles";
import { Theme, Grid, Typography } from "@material-ui/core";
import * as Yup from "yup";
import clsx from "clsx";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import useStudentDataInfo from "../../../hooks/student-data-info/useStudentDataInfo";
import TrTextField from "../../shared/controls/TrTextField";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import StepHandler from "../../../models/shared/StepHandler";
import PaymentRequestStudent from "../../../models/payment-request/PaymentRequestStudent";
import PaymentRequestStudentData from "../../../models/payment-request/PaymentRequestStudentData";
import TrackerName from "../../../models/payment-request/PaymentRequestTrackerName";
import StudentCostData from "../../../models/payment-request/StudentCostData";
import StudentNumberOfDays from "../../../models/payment-request/StudentNumberOfDays";
import { ProgramType } from "../../../models/financing-request/ProgramType";
import PaymentRequestApiClient from "../../../api/PaymentRequestApiClient";
import { nameof } from "../../../utilities/utilities";
import { currencyFormat, hrDate } from "../../../utilities/utilities";
import Currency from "../../../models/shared/Currency";
import AnnualAdministration from "../../../models/admin/annual-administration/AnnualAdministration";

interface Props extends StepHandler {
  students: PaymentRequestStudent[];
  studentsDataState: PaymentRequestStudentData[];
  schoolYearId: number;
  month?: Date | null;
  currency: Currency;
  annualAdministration: AnnualAdministration;
  updateStudentsData: (students: PaymentRequestStudentData[]) => void;
  setLatestStudentsData?: (data: PaymentRequestStudentData[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    column: {
      display: "flex",
      alignItems: "start",
      flexDirection: "column",
      flexGrow: 1,
      flexBasis: 0,
    },
    largeColumn: {
      display: "flex",
      alignItems: "start",
      flexDirection: "column",
      flexGrow: 1.5,
      flexBasis: 0,
    },
    lastColumn: {
      display: "flex",
      alignItems: "start",
      flexDirection: "column",
      flexGrow: 2,
      flexBasis: 0,
    },
    container: {
      justifyContent: "space-between",
      padding: "10px 0",
    },
    error: {
      color: theme.palette.error.main,
    },
  })
);

export default function PaymentRequestStudentDataStep({
  students,
  studentsDataState,
  schoolYearId,
  month,
  currency,
  annualAdministration,
  updateStudentsData,
  setLatestStudentsData,
  buttons,
  onPrevious,
  onNext,
  goToPreviousStep,
  goToNextStep,
  onStepperSave,
}: Props) {

  const classes = useStyles({});

  console.log("PaymentRequestStudentDataStep - " + annualAdministration.elementarySchoolTeachingAidPrice)

  const [studentsData, setStudentsData] = React.useState<
    PaymentRequestStudentData[]
  >(new Array<PaymentRequestStudentData>());

  const [studentsCostData, setStudentsCostData] = React.useState<
    StudentCostData[]
  >(new Array<StudentCostData>());

  const [shouldUpdatePrices, setShouldUpdatePrices] = React.useState<boolean>(
    false
  );

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    message: snackbarMessage,
    close: closeSnackbar,
    variant: snackbarVariant,
  } = useSnackbar();

  useEffect(() => {
    if (setLatestStudentsData) {
      setLatestStudentsData(studentsData);
    }
  }, [studentsData]);
  

  useEffect(() => {

    async function getStudents() {
      let newStudentsData = new Array<PaymentRequestStudentData>();
      students.forEach((student) => {
        if (student.isSelected) {
          newStudentsData.push({
            student: student,
          });
        }
      });

      setStudentsData(newStudentsData);
      if (studentsCostData.length == 0) getStudentCostData(newStudentsData);
    }

    async function getStudentCostData(
      newStudentsData: PaymentRequestStudentData[]
    ) {
      const apiClient = new PaymentRequestApiClient();
      let studentNumberOfDays = new Array<StudentNumberOfDays>();
      newStudentsData.forEach((studentData) => {
        const days = studentData.numberOfDays || 0;
        studentNumberOfDays.push({
          studentId: studentData.student.id,
          numberOfDays: days,
        });
      });

      const studentsCostData = await apiClient.getStudentsCostDataForNewPaymentRequest(
        schoolYearId,
        studentNumberOfDays,
        month!
      );

      setStudentsCostData(studentsCostData);
      setShouldUpdatePrices(true);
    }

    if (studentsDataState.length > 0) {
      setStudentsData([...studentsDataState]);
    } else if (studentsData.length == 0) {
      getStudents();
    } else {
      setStudentsData([...studentsDataState]);
    }
  }, [studentsDataState]);

  useEffect(() => {
    if (shouldUpdatePrices) {
      handleUpdatePrices();
    }
  }, [shouldUpdatePrices]);

  const paymentRequestValidationConfig = {
    trackerName: TrackerName.StudentData,
    validationScheme: Yup.array().of(
      Yup.object<PaymentRequestStudentData[]>().shape({
        numberOfDays: Yup.number()
          .nullable()
          .min(0, "Broj dana mora biti između 0-24.")
          .max(24, "Broj dana mora biti između 0-24.")
          .required(),
      })
    ),
  } as ValidationConfiguration;

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors,
  } = useChangeTracker([paymentRequestValidationConfig], false);

  useEffect(() => {
    return updateTrackerDebounceEffect([
      [TrackerName.StudentData, studentsData],
    ]);
  }, [studentsData]);

  function getStudentCostData(studentId: number) {
    return studentsCostData.find((sad) => sad.studentId == studentId);
  }

  const {
    getTransportOptionInfoByStudentId,
    getFoodOptionInfoByStudentId,
    getTeachingAidOptionInfoByStudentId,
  } = useStudentDataInfo({ schoolYearId: schoolYearId, month: month });

  async function handleGetCosts(e: React.FocusEvent<HTMLInputElement>) {
    const apiClient = new PaymentRequestApiClient();
    let studentNumberOfDays = new Array<StudentNumberOfDays>();
    studentsData.forEach((studentData) => {
      const days = studentData.numberOfDays || 0;
      studentNumberOfDays.push({
        studentId: studentData.student.id,
        numberOfDays: days,
      });
    });

    const studentsCostData = await apiClient.getStudentsCostDataForNewPaymentRequest(
      schoolYearId,
      studentNumberOfDays,
      month!
    );

    setStudentsCostData(studentsCostData);
    setShouldUpdatePrices(true);
  }

  const handleChange = (studentData: PaymentRequestStudentData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(e.target.value);
  
    setStudentsData((prevStudentsData) =>
      prevStudentsData.map((prevStudentData) => {
        if (prevStudentData.student.id === studentData.student.id) {
          return {
            ...prevStudentData,
            numberOfDays: isNaN(value) ? null : value,
          };
        }
        return prevStudentData;
      })
    );
  };
  

  function handleUpdatePrices() {
    setStudentsData((prevStudentsData) =>
      prevStudentsData.map((prevStudentData) => {
        const costData = getStudentCostData(prevStudentData.student.id);
        return {
          ...prevStudentData,
          foodCost: costData ? costData.foodCost : 0,
          teachingAidCost: costData ? costData.teachingAidCost : 0,
          transportCost: costData ? costData.transportCost : 0,
        };
      })
    );

    setShouldUpdatePrices(false);
  }

  const handleNextStep = () => {
    if (!validationResult.isValid(false)) {
      showErrors();

      const validationMessages = validationResult
        .getTrackerMessages(TrackerName.StudentData, false)
        .filter(
          (m) => m.validationType === "min" || m.validationType === "max"
        );

      let message = "";
      if (validationMessages.length > 0) {
        message = validationMessages[0].message;
      } else {
        message =
          "Ne možete prijeći na sljedeći korak dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima.";
      }

      openSnackbar(message, "error");
    } else {
      updateStudentsData(studentsData);
      goToNextStep && goToNextStep();
    }
  };

  const handlePreviousStep = () => {
    if (goToPreviousStep != null) {
      updateStudentsData(studentsData);
      goToPreviousStep();
    }
  };

  const handleUpdateState = () => {
    updateStudentsData([...studentsData]);
  };
  

  onPrevious && onPrevious(handlePreviousStep);
  onNext && onNext(handleNextStep);
  onStepperSave && onStepperSave(handleUpdateState);
  return (
    <>
      {studentsData.map((studentData, index) => (
        <ExpansionPanel key={studentData.student.id}>
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            className={clsx(
              !validationResult.isArrayItemValid(
                TrackerName.StudentData,
                index.toString()
              ) && classes.error
            )}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Grid container spacing={4} className={classes.container}>
              <Grid item lg="auto" md={4} className={classes.column}>
                <Typography variant="body2" color="textSecondary">
                  Učenik
                </Typography>
                <Typography variant="subtitle2">
                  {studentData.student.lastName} {studentData.student.firstName}
                </Typography>
                <Typography variant="subtitle2">
                  {hrDate(studentData.student.birthDate)}
                </Typography>
              </Grid>
              <Grid item lg="auto" sm={4} className={classes.column}>
                <Typography variant="body2" color="textSecondary">
                  OIB
                </Typography>
                <Typography variant="subtitle2">
                  {studentData.student.oib}
                </Typography>
              </Grid>
              <Grid item lg="auto" sm={3} className={classes.largeColumn}>
                <Typography variant="body2" color="textSecondary">
                  Razred
                </Typography>
                <Typography variant="subtitle2">
                  {studentData.student.programName}
                </Typography>
                <Typography variant="subtitle2">
                  {studentData.student.schoolClass +
                    " - " +
                    studentData.student.sectionMark}
                </Typography>
              </Grid>
              <Grid item lg="auto" sm={3} className={classes.largeColumn}>
                <Typography variant="body2" color="textSecondary">
                  Vrsta odjeljenja
                </Typography>
                <Typography variant="subtitle2">
                  {studentData.student.sectionTypeName}
                </Typography>
              </Grid>
              <Grid item lg="auto" sm={3} className={classes.column}>
                <Typography variant="body2" color="textSecondary">
                  Prijevoz
                </Typography>
                <Typography variant="subtitle2">
                  {getTransportOptionInfoByStudentId(studentData.student.id, currency)}
                </Typography>
                <Typography variant="subtitle2">
                  Broj dana: {studentData.numberOfDays || 0}
                  {", Cijena: " +
                    currencyFormat(studentData.transportCost || 0, currency)}
                </Typography>
              </Grid>
              {studentData.student.programType == ProgramType.Elementary ? (
                <Grid item lg="auto" sm={3} className={classes.column}>
                  <Typography variant="body2" color="textSecondary">
                    Prehrana
                  </Typography>
                  <Typography variant="subtitle2">
                    {getFoodOptionInfoByStudentId(studentData.student.id)}
                  </Typography>
                  <Typography variant="subtitle2">
                    Broj dana: {studentData.numberOfDays || 0}
                    {", Cijena: " + currencyFormat(studentData.foodCost || 0, currency)}
                  </Typography>
                </Grid>
              ) : (
                <></>
              )}

              <Grid item lg="auto" sm={3} className={classes.column}>
                <Typography variant="body2" color="textSecondary">
                  Posebna nastavna sredstva i pomagala
                </Typography>
                <Typography variant="subtitle2">
                  {getTeachingAidOptionInfoByStudentId(
                    studentData.student.id,
                    studentData.student.programType,
                    studentData.student.specialNeedType,
                    currency,
                    annualAdministration
                  ) }
                </Typography>
                <Typography variant="subtitle2">
                  Broj dana: {studentData.numberOfDays || 0}
                  {", Cijena: " +
                    currencyFormat(studentData.teachingAidCost || 0, currency)}
                </Typography>
              </Grid>
            </Grid>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <Grid container spacing={4}>
              <Grid item lg={3} md={6} sm={12} className={classes.column}>
                <TrTextField
                  required
                  label="Broj dana u školi"
                  name={nameof<typeof studentData>("numberOfDays")}
                  value={
                    studentData.numberOfDays != null
                      ? studentData.numberOfDays
                      : ""
                  }
                  onChange={handleChange(studentData)}
                  onBlur={handleGetCosts}
                  type="number"
                  className="textField"
                  error={
                    !validationResult.isArrayPropertyValid(
                      TrackerName.StudentData,
                      index.toString(),
                      nameof<typeof studentData>("numberOfDays")
                    )
                  }
                />
              </Grid>
            </Grid>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      ))}
      {buttons}
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </>
  );
}
